package ai.wagemix.web.screens.sharedmix

import ai.wagemix.logic.sharedmix.WMDefaultSharedMixPresenter
import ai.wagemix.logic.sharedmix.WMSharedMixError
import ai.wagemix.logic.sharedmix.WMSharedMixPresentation
import ai.wagemix.logic.sharedmix.WMSharedMixPresenter
import ai.wagemix.logic.sharedmix.WMSharedMixPresenter.Action
import ai.wagemix.logic.sharedmix.WMSharedMixPresenter.Event
import ai.wagemix.net.models.WMBadge
import ai.wagemix.web.components.WMBandChart
import ai.wagemix.web.components.WMBandChartSize
import ai.wagemix.web.components.WMBandChartModel
import ai.wagemix.web.components.WMButton
import ai.wagemix.web.components.WMChartCard
import ai.wagemix.web.components.WMChartSource
import ai.wagemix.web.components.WMEmptyState
import ai.wagemix.web.components.WMIconGlyph
import ai.wagemix.web.components.WMKeyValue
import ai.wagemix.web.components.WMPanel
import ai.wagemix.web.components.WMStatTile
import ai.wagemix.web.components.WMTierBadge
import ai.wagemix.web.screens.WMWebStrings
import ai.wagemix.web.screens.formatShare
import ai.wagemix.web.screens.formatWage
import ai.wagemix.web.screens.formatWageRange
import ai.wagemix.web.screens.toBandChartModel
import ai.wagemix.web.theme.WMAppContentGapVar
import ai.wagemix.web.theme.WMBodyTextStyle
import ai.wagemix.web.theme.WMLeadTextStyle
import ai.wagemix.web.theme.WMPageTitleTextStyle
import ai.wagemix.web.theme.WMStatTileGapVar
import ai.wagemix.web.theme.WMPreviewHeightVar
import ai.wagemix.web.theme.WMPreviewWidthVar
import ai.wagemix.web.theme.WMPreviewSummaryWidthVar
import org.jetbrains.compose.web.css.px
import com.varabyte.kobweb.compose.ui.modifiers.minWidth
import com.varabyte.kobweb.compose.ui.modifiers.width
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxHeight
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxSize
import com.varabyte.kobweb.compose.foundation.layout.Row
import androidx.compose.runtime.Composable
import org.jetbrains.compose.web.dom.Div
import com.varabyte.kobweb.compose.ui.toAttrs
import androidx.compose.runtime.State
import androidx.compose.runtime.derivedStateOf
import androidx.compose.runtime.mutableStateOf
import com.fsryan.foundation.web.screens.FSWebScreen
import com.fsryan.foundation.web.screens.FSWebViewModel
import com.fsryan.foundation.web.screens.FSWebViewStateHolder
import com.fsryan.foundation.web.theme.FSBreakpoint
import com.fsryan.foundation.web.theme.fs
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.color
import com.varabyte.kobweb.compose.ui.modifiers.display
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.gap
import com.varabyte.kobweb.compose.ui.modifiers.gridTemplateColumns
import com.varabyte.kobweb.compose.ui.modifiers.id
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.style.CssStyle
import com.varabyte.kobweb.silk.style.toModifier
import com.varabyte.kobweb.silk.theme.colors.ColorMode
import com.varabyte.kobweb.silk.theme.colors.palette.toPalette
import org.jetbrains.compose.web.css.DisplayStyle
import org.jetbrains.compose.web.css.fr

interface WMSharedMixWebViewStateHolder: FSWebViewStateHolder {
    val mixState: State<WMSharedMixPresentation?>
    val errorState: State<WMSharedMixError?>
}

abstract class WMBaseSharedMixWebViewModel<E: Event, A: Action, PR: WMSharedMixPresenter<E, A>>(presenter: PR):
    FSWebViewModel<E, A, PR>(presenter), WMSharedMixWebViewStateHolder {

    override val mixState: State<WMSharedMixPresentation?> get() = _mixState
    protected val _mixState = mutableStateOf<WMSharedMixPresentation?>(null)

    override val errorState: State<WMSharedMixError?> get() = _errorState
    protected val _errorState = mutableStateOf<WMSharedMixError?>(null)

    val chartModelState: State<WMBandChartModel?> = derivedStateOf { _mixState.value?.summary?.toBandChartModel() }

    override fun customEventHandling(event: E) {
        super.customEventHandling(event)
        with(event) {
            mixUpdate?.let { _mixState.value = it }
            errorUpdate?.let { _errorState.value = it }
        }
    }
}

typealias WMDefaultSharedMixWebViewModel = WMBaseSharedMixWebViewModel<Event.Base, Action.Base, WMDefaultSharedMixPresenter>

fun createDefaultSharedMixWebViewModel(presenter: WMDefaultSharedMixPresenter): WMDefaultSharedMixWebViewModel =
    object: WMDefaultSharedMixWebViewModel(presenter) {}

val WMSharedStatTilesStyle = CssStyle {
    base { Modifier.fillMaxWidth().display(DisplayStyle.Grid).gridTemplateColumns { repeat(1) { size(1.fr) } }.gap(WMStatTileGapVar.value()) }
    FSBreakpoint.Medium { Modifier.gridTemplateColumns { repeat(3) { size(1.fr) } } }
}

/**
 * The DOM id the API's snapshotter waits for in preview mode. It appears once the chart has a model to draw; the
 * snapshotter then waits for the chart's own canvas, since the chart embed reports no "drawn" event.
 */
const val WM_PREVIEW_READY_ID = "preview-ready"

@Composable
fun <E: Event, A: Action, PR: WMSharedMixPresenter<E, A>, VM: WMBaseSharedMixWebViewModel<E, A, PR>> WMSharedMixScreen(
    createViewModel: () -> VM,
    sharedMixId: String?,
    strings: WMWebStrings,
    /** `?renderingMode=preview`: the 1200×630 card the API snapshots. */
    preview: Boolean = false,
    modifier: Modifier = Modifier,
    /** The "Try Wagemix" call to action; the page navigates (and reports the click). */
    onCtaClick: () -> Unit
) {
    FSWebScreen(createViewModel = createViewModel, key = sharedMixId) { vm ->
        WMSharedMixScreenLayout(
            strings = strings,
            loadingState = vm.loadingState,
            mixState = vm.mixState,
            chartModelState = vm.chartModelState,
            errorState = vm.errorState,
            preview = preview,
            modifier = modifier,
            onCtaClick = onCtaClick,
        )
    }
}

@Composable
fun WMSharedMixScreenLayout(
    strings: WMWebStrings,
    loadingState: State<Boolean>,
    mixState: State<WMSharedMixPresentation?>,
    chartModelState: State<WMBandChartModel?>,
    errorState: State<WMSharedMixError?>,
    preview: Boolean,
    modifier: Modifier = Modifier,
    onCtaClick: () -> Unit
) {
    val sm = strings.sharedMix
    val colors = ColorMode.current.toPalette().fs
    val error = errorState.value
    val mix = mixState.value
    Column(modifier = Modifier.fillMaxWidth().gap(WMAppContentGapVar.value()).then(modifier)) {
        when {
            error != null -> {
                val (title, message) = when (error) {
                    WMSharedMixError.InvalidLink -> sm.invalidTitle to sm.invalidMessage
                    WMSharedMixError.NotFound -> sm.notFoundTitle to sm.notFoundMessage
                    WMSharedMixError.Deactivated -> sm.deactivatedTitle to sm.deactivatedMessage
                    WMSharedMixError.Failed -> sm.failedTitle to sm.failedMessage
                }
                WMEmptyState(WMIconGlyph.TriangleAlert, title, message) {
                    WMButton(sm.ctaButton, onClick = onCtaClick)
                }
            }
            mix == null -> SpanText(strings.common.loading, WMBodyTextStyle.toModifier().color(colors.onSurfaceVariant))
            preview -> WMSharedMixPreviewCard(strings, mix, chartModelState.value)
            else -> {
                val h = mix.summary.headline
                SpanText(mix.title, WMPageTitleTextStyle.toModifier())
                WMPanel(title = sm.wageAnalysis) {
                    if (mix.badge != WMBadge.None) {
                        WMTierBadge(mix.badge, strings.share.tierName(mix.badge), strings.share.tierExplanation(mix.badge))
                    }
                    val low = mix.payRangeLow
                    val high = mix.payRangeHigh
                    if (low != null && high != null) {
                        WMKeyValue(sm.payRange, formatWageRange(low.toDouble(), high.toDouble(), mix.hourly))
                    }
                    listOfNotNull(mix.areaName, mix.industryName).takeIf { it.isNotEmpty() }?.let { WMKeyValue(strings.mixBuilder.filters, it.joinToString(" · ")) }
                }
                Div(WMSharedStatTilesStyle.toModifier().toAttrs()) {
                    WMStatTile(strings.mixBuilder.median, formatWage(h.median, mix.hourly), caption = h.year.toString(), highlight = true)
                    WMStatTile(strings.mixBuilder.middleHalf, formatWageRange(h.p25, h.p75, mix.hourly), caption = h.year.toString())
                    WMStatTile(strings.mixBuilder.typicalRange, formatWageRange(h.p10, h.p90, mix.hourly), caption = h.year.toString())
                }
                WMChartCard(title = strings.mixBuilder.chartTitle, footer = { WMChartSource(strings.common.dataSource) }) {
                    chartModelState.value?.let { WMBandChart(model = it, description = strings.mixBuilder.chartDescription, size = WMBandChartSize.fixedHeight, eager = true) }
                }
                WMPanel(title = sm.occupations) {
                    mix.occupations.forEach { WMKeyValue(it.title, formatShare(it.share)) }
                }
                WMPanel(title = sm.aboutTitle) { SpanText(sm.aboutText, WMBodyTextStyle.toModifier().color(colors.onSurfaceVariant)) }
                WMPanel(title = sm.ctaTitle) {
                    SpanText(sm.ctaMessage, WMLeadTextStyle.toModifier().color(colors.onSurfaceVariant))
                    WMButton(sm.ctaButton, leadingIcon = WMIconGlyph.ArrowRight, onClick = onCtaClick)
                }
            }
        }
    }
}

/**
 * The 1200×630 card the API snapshots for link previews ([WMPreviewWidthVar] × [WMPreviewHeightVar]): the mix's
 * title, badge, filters and headline figures on the left, its wage chart on the right. The chart loads eagerly, since
 * a lazy one outside the snapshot's viewport would never load, and [WM_PREVIEW_READY_ID] appears only once the chart
 * has a model.
 */
@Composable
private fun WMSharedMixPreviewCard(strings: WMWebStrings, mix: WMSharedMixPresentation, chartModel: WMBandChartModel?) {
    val colors = ColorMode.current.toPalette().fs
    val h = mix.summary.headline
    Row(Modifier.fillMaxSize().gap(WMAppContentGapVar.value())) {
        Column(Modifier.width(WMPreviewSummaryWidthVar.value()).fillMaxHeight().gap(WMAppContentGapVar.value())) {
            SpanText(mix.title, WMPageTitleTextStyle.toModifier())
            if (mix.badge != WMBadge.None) {
                WMTierBadge(mix.badge, strings.share.tierName(mix.badge), strings.share.tierExplanation(mix.badge))
            }
            listOfNotNull(mix.areaName, mix.industryName).takeIf { it.isNotEmpty() }?.let {
                SpanText(it.joinToString(" · "), WMBodyTextStyle.toModifier().color(colors.onSurfaceVariant))
            }
            Column(Modifier.fillMaxWidth().gap(WMStatTileGapVar.value())) {
                WMStatTile(strings.mixBuilder.median, formatWage(h.median, mix.hourly), caption = h.year.toString(), highlight = true)
                WMStatTile(strings.mixBuilder.middleHalf, formatWageRange(h.p25, h.p75, mix.hourly), caption = h.year.toString())
                WMStatTile(strings.mixBuilder.typicalRange, formatWageRange(h.p10, h.p90, mix.hourly), caption = h.year.toString())
            }
            WMChartSource(strings.common.dataSource)
        }
        Box(Modifier.fillMaxWidth().minWidth(0.px)) {
            chartModel?.let {
                WMBandChart(model = it, description = strings.mixBuilder.chartDescription, size = WMBandChartSize.preview, eager = true)
            }
        }
    }
    if (chartModel != null) {
        // The snapshotter polls for this id.
        Box(Modifier.id(WM_PREVIEW_READY_ID))
    }
}
